import { $models, $modelsLoader, ModelFactory } from '../common/$models'
import { $virtualModels, $virtualModelsLoader } from '../common/$virtualModels'
import { storeMethods } from '../common/storeMethods'

import { AxiosStatic } from 'axios'
import { App, ComponentPublicInstance } from 'vue'
import { Router } from 'vue-router'
import { CoreBaseClass } from '../common/$coreBaseClass'
import { $directusSdk, DirectusSdkType } from '../common/$directusSdk'
import { $uiState } from '../common/$uiState'
import { singletonInstanceSummoner } from '../common/singletonInstanceSummoner'
import { $obCache, ObjectCacheService } from '../plugins/$obCache/$obCache'
import { AppDefinitionLoaderService } from '../plugins/AppDefinitions/front/AppDefinitionLoaderService'
import { ComposableComponentSettingsManager } from '../plugins/ComposableComponents/$composableComponentSettingsManager'
import { RecordWorkflowManager } from '../plugins/RecordWorkflow/front/RecordWorkflowManager'
import { UserMetaDataService } from '../plugins/UserMetaDataService/front/$userMetaData'
import { UserNotificationsService } from '../plugins/UserNotifications/front/$userNotificationsService'
import { OrganizationsManager } from '../plugins/UserOrgGroupsManager/common/OrganizationsManager'
import { $componentStore, ComponentStoreService } from './$componentStore/$componentStore'
import { $errorReporter } from './$libs/$errorReporter/$errorReporter'
import { ImageTransformer } from './$libs/$imgix'
import { $indexedDbCache, IndexedDbCache } from './$libs/$indexedDbCache/$indexedDbCache'
import { $lsCache, LsCache } from './$libs/$lsCache'
import { $loading } from './$loading'
import { $toast } from './$toast/$toast'
import { $embAuth } from './auth/$embAuth/$embAuth'
import { DataExportService } from './DataExportSettings/$dataExportService'
import { ExportAsExcel } from './ExcelExport/$exportAsExcel'
import { $frontErrors } from './FrontErrors/$frontErrors'
import { $i18n, I18nService } from './I18nService/$i18n'
import {
  $inlineCssController,
  InlineCssController,
} from './InlineCssController/$inlineCssController'
import { $modals } from './Modal/$modals'
import { $fileManager } from './FileManager/FileManager'

// @ts-ignore ModelNameをstring型として定義し、namespacesから型を取得
declare global {
  export interface GlobalModelFactories {
    [key: string]: ModelFactory<any>
  }
}

/**
 * Frontのための $core 内の各種サービスインスタンスを保持するSingleton Instance
 * window.$core.$models などとしてアクセスするための中核
 * 各種共通で利用するModuleをInject
 * ここに列挙しているものは "デフォルトの" Modulesであり、まずもって必ずどのプロジェクトでも利用するものを付与している。
 * 各プラグインで、 globalThis.$core にプロパティを拡張していく考え方。
 *
 * @category $core
 */
export class Core extends CoreBaseClass {
  $modelsLoader: typeof $modelsLoader = $modelsLoader
  $virtualModelsLoader: typeof $virtualModelsLoader = $virtualModelsLoader
  $models: GlobalModelFactories = $models
  $storeMethods: typeof storeMethods = storeMethods
  /**
   * @hidden from docs
   */
  $directusSdk: DirectusSdkType = $directusSdk
  /**
   * Alias of $directusSdk
   * @hidden from docs
   */
  $d: DirectusSdkType = $directusSdk
  $axios: AxiosStatic
  $toast = $toast
  $loading = $loading
  $virtualModels = $virtualModels
  $modals = $modals
  $embAuth = $embAuth
  $uiState = $uiState
  $lsCache: LsCache = $lsCache
  $indexedDbCache: IndexedDbCache = $indexedDbCache
  $obCache: ObjectCacheService = $obCache
  $imgix?: ImageTransformer
  $errorReporter: typeof $errorReporter = $errorReporter
  $inlineCssController: InlineCssController = $inlineCssController
  /**
   * UserMetaData
   */
  $userMetaData: UserMetaDataService
  /**
   * @hidden from docs
   */
  $appDefinitionLoader: AppDefinitionLoaderService
  $dataExportService: DataExportService
  $exportAsExcel: ExportAsExcel
  $i18n: I18nService = $i18n
  /**
   * 通常の管理画面を利用しない場合にtrueにする, default false
   * 独自画面を構築したい場合に、いくつかのデフォルト挙動をoffにする (signInへのリダイレクト挙動など)
   */
  isSdkMode = false
  VueClass: App
  $router: Router & Record<string, any> & { currentRoute: any }
  $vueRoot: ComponentPublicInstance
  $root: ComponentPublicInstance
  isProduction: boolean
  $recordWorkflowManager: RecordWorkflowManager

  /**
   * Error types
   */
  $frontErrors = $frontErrors

  /**
   * Composable Components
   */
  $composableComponentSettingsManager: ComposableComponentSettingsManager

  $userNotificationsService: UserNotificationsService

  $componentStore: ComponentStoreService = $componentStore

  /**
   * 組織管理
   */
  $organizationsManager: OrganizationsManager

  $fileManager : typeof $fileManager = $fileManager

  constructor() {
    super()
    // @ts-ignore
    this.$axios = this.$d.transport.axios
    this.$organizationsManager = new OrganizationsManager()
  }

  /**
   * @hidden from docs
   */
  static get instance(): Core {
    // return makeItReactive(singletonInstanceSummoner('CORE', Core))
    return singletonInstanceSummoner('CORE', Core)
  }

  /**
   * Serviceを注入する
   * window.$core.$serviceName で利用可能になる
   * TODO: これ全然使っていないが... どうしようか
   * @param serviceName
   * @param serviceInstance
   * @hidden from docs
   */
  injectService(serviceName: string, serviceInstance) {
    this[serviceName] = serviceInstance
  }
}

const coreInstance = Core.instance
// @ts-ignore
if (!globalThis.$core) {
  // @ts-ignore
  globalThis.$core = coreInstance as Core
}

// To avoid error(s) from some libs
if (!globalThis.global) {
  // @ts-ignore
  globalThis.global = globalThis
}
export const $core = coreInstance as Core
